// ?modal=add-expense (DD-045, TD-075, TD-077): a bill paid once, recorded paid, or a recurring expense from its
// first bill. EUR only for now: the ECB rate book comes with the first cost in another currency. A one-off carries a
// request key, so a double click records once; a recurring one bills from its first date and shows in Money.
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { ModalPending } from "../components/ModalPending";
import { MissingLine, Required } from "../components/Required";
import { WithoutIva } from "../components/WithoutIva";
import { useAddTemplate, useExpenseCategories, useRecordExpense } from "../hooks/useExpenses";
import { useClientContracts, useServiceLines } from "../hooks/usePricing";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import { useClientSummaries } from "../hooks/useReports";
import type { ClientSummary, ExpenseCategory, ServiceLine } from "../types";
import {
  REPEAT_OPTIONS, aYearAfter, dateOnRepeat, expenseCheck, expenseMissing, expenseRequest, firstDate, liveCategory, toRepeats, type ExpenseForm, type Repeats,
} from "../utils/expenses";
import { verbAmount } from "../utils/format";
import { closeUnlessPending, shownError } from "../utils/mutations";
import { livePass, NO_PASS } from "../utils/passedCosts";
import { newRequestKey } from "../utils/requestKey";
import { CategoryField, ClientField, EXPENSE_LISTS_FAILED, PassAlongField, ServiceLineField } from "./ExpenseFields";

const EXPENSE = ENTITY_CONFIG.expense;

export function ExpenseModal({ onClose }: { onClose: () => void }) {
  const categories = useExpenseCategories();
  const lines = useServiceLines();
  const clients = useClientSummaries();
  if (!categories.data || !lines.data || !clients.data) {
    return (
      <ModalPending
        size="lg"
        title={EXPENSE.createLabel}
        failure={categories.isError || lines.isError || clients.isError ? EXPENSE_LISTS_FAILED : null}
        retrying={categories.isFetching || lines.isFetching || clients.isFetching}
        retry={() => {
          if (!categories.data) void categories.refetch();
          if (!lines.data) void lines.refetch();
          if (!clients.data) void clients.refetch();
        }}
        onClose={onClose}
      />
    );
  }
  return <AddExpenseForm categories={categories.data} lines={lines.data} clients={clients.data.clients} today={clients.data.today} onClose={onClose} />;
}

type Fields = Omit<ExpenseForm, "pending">;

interface FormProps {
  categories: ExpenseCategory[];
  lines: ServiceLine[];
  clients: ClientSummary[];
  today: string;
  onClose: () => void;
}

function AddExpenseForm({ categories, lines, clients, today, onClose }: FormProps) {
  const record = useRecordExpense();
  const add = useAddTemplate();
  const pending = record.isPending || add.isPending;
  const [form, setForm] = useState<Fields>(() => ({
    label: "",
    amount: null,
    varies: false,
    repeats: "monthly",
    // A bill day the form takes, even on the 29th to the 31st.
    date: firstDate("monthly", today),
    category: { id: categories.length > 0 ? categories[0].id : null, newLabel: "" },
    clientId: "",
    serviceLineId: "",
    pass: NO_PASS,
  }));
  // One key per modal open: a double click or a retry of the same bill records it once.
  const [requestId] = useState(newRequestKey);
  // A required field shows its error once it was touched; the form opens clean, with the primary disabled meanwhile.
  const [touched, setTouched] = useState({ label: false, amount: false });
  const patch = (p: Partial<Fields>) => setForm((f) => ({ ...f, ...p }));
  const changeRepeats = (repeats: Repeats) =>
    setForm((f) => ({ ...f, repeats, varies: false, date: dateOnRepeat(f.date, f.repeats, repeats, today) }));
  // A category deleted meanwhile drops from the pick, so the form asks for another instead of being refused again.
  const category = liveCategory(form.category, categories);
  const contractsQuery = useClientContracts(form.clientId || undefined);
  const own = contractsQuery.data?.ownClient ?? false;
  // The agency's own client is never billed (TD-105): the checks and the request read the box as off.
  const fields = { ...form, pass: livePass(form.pass, own), category, pending };
  const check = expenseCheck(fields, today);
  const once = form.repeats === "once";
  const verb = once ? `Record ${EXPENSE.label.toLowerCase()}` : EXPENSE.createLabel;
  // A bill paid now records money, so its button carries the amount (DD-056). The recurring one records none.
  const primary = once ? verbAmount(verb, form.amount, "EUR") : verb;
  const missing = expenseMissing({ ...form, category }, once);
  const send = useSubmitOnce();
  const submit = () =>
    send(
      (): Promise<unknown> => {
        const request = expenseRequest(fields);
        return request.kind === "once" ? record.mutateAsync({ input: request.input, requestId }) : add.mutateAsync(request.input);
      },
      () => onClose(),
    );

  return (
    <FinanceDrawer
      onClose={closeUnlessPending(pending, onClose)}
      size="lg"
      title={EXPENSE.createLabel}
      footer={
        <>
          <Button onClick={submit} disabled={check.blocked} isLoading={pending}>{primary}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label={<Required>What it is</Required>} error={shownError(check.labelError, touched.label)}>
          <input
            className={inputClassName}
            aria-required="true"
            value={form.label}
            onChange={(e) => patch({ label: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, label: true }))}
            placeholder="e.g. Adobe Creative Cloud"
            autoFocus
          />
        </FormField>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Repeats">
            <select className={inputClassName} value={form.repeats} onChange={(e) => changeRepeats(toRepeats(e.target.value))}>
              {REPEAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label={<Required>{once ? "Paid on" : "First bill on"}</Required>} hint={once ? undefined : "Its day of the month, 1 to 28, is the bill day"} error={check.dateError}>
            <input type="date" className={inputClassName} aria-required="true" value={form.date} max={once ? today : aYearAfter(today)} onChange={(e) => patch({ date: e.target.value })} />
          </FormField>
        </div>
        <FormField label={form.varies ? "Amount" : <Required>Amount</Required>} hint={form.varies ? "Each bill's amount is entered when it is marked paid" : "EUR only for now"} error={shownError(check.amountError, touched.amount)}>
          {form.varies ? (
            <div className="h-9 flex items-center text-sm text-gray-500">Varies</div>
          ) : (
            <>
              <MoneyInput
                valueMinor={form.amount}
                currency="EUR"
                onChange={(amount) => {
                  setTouched((t) => ({ ...t, amount: true }));
                  patch({ amount });
                }}
                invalid={touched.amount && !!check.amountError}
                ariaLabel="Amount"
                required
              />
              <WithoutIva />
            </>
          )}
        </FormField>
        {!once && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.varies} onChange={(e) => patch({ varies: e.target.checked })} />
            The amount varies each time
          </label>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CategoryField categories={categories} value={category} onChange={(picked) => patch({ category: picked })} error={check.categoryError} />
          <ClientField clients={clients} value={form.clientId} onChange={(clientId) => patch({ clientId, pass: clientId ? form.pass : NO_PASS })} />
          <ServiceLineField lines={lines} value={form.serviceLineId} onChange={(serviceLineId) => patch({ serviceLineId })} />
        </div>
        <PassAlongField clientId={form.clientId} own={own} value={form.pass} onChange={(pass) => patch({ pass })} markupError={check.markupError} />
        <MissingLine button={verb} missing={missing} />
      </div>
    </FinanceDrawer>
  );
}
